import { useContext, useEffect, useMemo, useState } from 'react';
import {
  ButtonIcon,
  CardWrapperWithHeader,
  formatUnixTime,
  getCoverageByValue,
  getStationByKey,
  Heading,
  isClaimWarrantyExpired,
  Modal,
  Table,
} from '@rabbit/elements/shared-components';
import {
  ArrowTopRightOnSquareIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { ModalNewEditCustomer } from '../components/organisms/ModalNewEditCustomer/ModalNewEditCustomer';
import { ModalChangeEmail } from '../components/organisms/ModalChangeEmail/ModalChangeEmail';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useConsumerProfile,
  useGetConsumerData,
  useManageFaults,
} from '@rabbit/bizproc/react';
import { add, fromUnixTime, getUnixTime, sub } from 'date-fns';
import {
  capitalizeFirstLetter,
  getFavouriteStore,
  getProductCategoryFromHoldingData,
  getProductTitleFromHoldingData,
  ONCE,
} from '../utils/helpers';
import {
  DTCaseFlowPrivateProxy,
  DTTenant_Public,
  ProductQuestionnaireResponses,
  SELF_REGISTERED_VENDABLE,
  Warranty,
  WarrantyStatus
} from '@rabbit/data/types';
import placeholderImg from '../assets/images/placeholder-image.png';
import CustomerDetailsContentSection from '../components/molecules/CustomerDetailsContentSection/CustomerDetailsContentSection';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { BL_Warranty } from '@rabbit/bizproc/core';
import { CORPORATE } from '@rabbit/sage/utils/consts.ts';
import BusinessDetailsContentSection from '@rabbit/sage/components/molecules/CustomerDetailsContentSection/BusinessDetailsContentSection.tsx';
import { CreateTable } from '../utils/CreateTable';
import { AppContext } from '@rabbit/app-context';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import { Config_ClaimDetailViewType } from '@rabbit/config/enums';
import {
  buildFaultIdToTitleMap,
  getAssessedFaultText,
  getClaimCostsTotalText,
  getDefectivePartManufacturerText,
  getDefectivePartText,
  getGoodwillOverrideFlag,
  getProductNameText,
  getReplacementPartText,
  getWarrantyAssessmentLabel,
  getWarrantyCostText,
  hasVehicleNumber,
} from '../utils/claimTableUtils';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface CustomerDetailViewProps { }

export function CustomerDetailView(props: CustomerDetailViewProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id: consumerId } = useParams<'id'>();
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] =
    useState<ProductQuestionnaireResponses | null>(null);
  const { config, tenantInfo } = useContext(AppContext);
  // Avoid a live Firestore subscription just to render/export fault titles.
  const { faultList } = useManageFaults(undefined, { subscribe: false });

  const faultIdToTitle = useMemo(() => {
    return buildFaultIdToTitleMap((faultList as any)?.data);
  }, [(faultList as any)?.data]);

  const { allHoldings, claims } =
    useGetConsumerData(null, consumerId, 0, t('tenantLink')) ?? {};
  const {
    //identityData,
    consumerPersonaData,
    error,
  } = useConsumerProfile(consumerId);

  useEffect(() => {
    // If there is a creation attempt error, the customer does not exist, so we redirect to the customers list
    if (error?.class === 'CREATION') navigate('/manage?tab=customers');
  }, [error]);

  const checkWarrantyStatus = (
    purchase_time: number,
    warranties: Warranty[]
  ) => {
    const warranty = BL_Warranty.getLatestWarranty(warranties);
    if (!purchase_time || !warranty) return '-';
    if (warranty.status === WarrantyStatus.VOIDED) return t('general.voided');
    const expiry_date =
      warranty?.endDate ||
      getUnixTime(
        sub(
          add(fromUnixTime(purchase_time), {
            [warranty.duration.division]: warranty.duration.amount,
          }),
          {
            days: 1,
          }
        )
      );
    if (!expiry_date) return '-';
    if (expiry_date > Date.now()) return 'In warranty';
    if (expiry_date < Date.now()) return 'Warranty expired';
  };

  const getProductName = (result: DTCaseFlowPrivateProxy) =>
    getProductNameText(result);

  const getWarrantyAssessment = (result: DTCaseFlowPrivateProxy) =>
    getWarrantyAssessmentLabel(result);

  const getAssessedFault = (result: DTCaseFlowPrivateProxy) =>
    getAssessedFaultText(result, faultIdToTitle);

  const consumerHoldingsData = !allHoldings
    ? []
    : allHoldings?.map((item) => {
      return {
        link: item?.docid,
        productImage: (
          <div
            className={classNames(
              'relative h-[120px] w-[120px] overflow-hidden bg-contain bg-center bg-no-repeat'
            )}
            style={{
              backgroundImage:
                'url(' + (item?.img?.[0] ?? placeholderImg) + ')',
            }}
          ></div>
        ),
        productName: getProductTitleFromHoldingData(item),
        category: capitalizeFirstLetter(
          getProductCategoryFromHoldingData(item)
        ),
        warrantyStatus: checkWarrantyStatus(
          item.purchase_time ?? 0,
          item.warranties
        ),
        purchaseDate: item?.purchase_time, //formatUnixTime(item?.register_time, 'dd/MM/yyyy'),
        registrationDate: item?.register_time ?? 0, //formatUnixTime(item?.register_time, 'dd/MM/yyyy'),
        ownershipEndDate: item?.purchase_time
          ? formatUnixTime(item?.purchase_time, 'dd/MM/yyyy')
          : '-',
        // TODO: manual will have a different shape soon
        userManual:
          item?.manual && item?.manual?.length > 0 ? (
            <div className="flex items-center">
              <p className="mr-3">Link</p>
              <a
                href={String(item?.manual) ?? '#'}
                target="_blank"
                rel="noreferrer"
              >
                <ArrowTopRightOnSquareIcon className="text-primary-900 h-4 w-4" />
              </a>
            </div>
          ) : (
            '-'
          ),
        survey: item?.questionnaire ? (
          <div
            className="flex cursor-pointer items-center"
            onClick={() => {
              setSelectedQuestionnaire(
                item?.questionnaire as ProductQuestionnaireResponses
              );
            }}
          >
            <p className="mr-3">View details</p>
            <div>
              <ArrowTopRightOnSquareIcon className="text-primary-900 h-4 w-4" />
            </div>
          </div>
        ) : (
          '-'
        ),
      };
    });

  const hasVehicleNo = useMemo(() => hasVehicleNumber(claims || []), [claims]);

  const ClaimsTable = CreateTable<DTCaseFlowPrivateProxy>(claims || [], [
    {
      header: t('general.id'),
      value: (data) => data?.docid.slice(0, 5).toUpperCase(),
    },
    {
      header: 'Registration ID',
      value: (data) => data?.facts?.consumer_holding ?? '-',
      Cell: ({ cell }: any) => {
        const value = cell.getValue();
        return typeof value === 'string' && value.length > 0
          ? value.slice(0, 5).toUpperCase()
          : '-';
      },
      filterVariant: 'text',
    },
    {
      header: 'Warranty ID',
      value: (data) => data?.facts?.warranty_link?.slice(0, 5).toUpperCase() ?? '-',
    },
    {
      header: t('general.inWarranty'),
      type: 'Status',
      value: (data) =>
        isClaimWarrantyExpired(
          data.facts.purchase_date,
          data.facts.holding_warranty_term
        ),
    },
    {
      header: t('general.assignedTo'),
      value: (data) => data.facts?.delegate_repairer_name || '-',
    },
    {
      header: t('general.claimsStatus'),
      value: (data) => getStationByKey(data.case_state, t).label ?? '-',
    },
    ...(config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS.vehicleNo && hasVehicleNo
      ? [
        {
          header: 'Vehicle No',
          value: (data: DTCaseFlowPrivateProxy) =>
            data?.facts?.additionalData?.vehicleNo ??
            '-',
          filterVariant: 'text' as const,
        },
      ]
      : []),
    ...(config.CLAIMS.CLAIM_DETAIL_VIEW.TYPE !==
      Config_ClaimDetailViewType.CAR_DETAILS ?
      [
        {
          header: t('general.product'),
          value: (data: any) => getProductName(data),
          primary: true,
        }] : []),
    {
      header: 'Cost of warranty',
      value: (data: DTCaseFlowPrivateProxy) => getWarrantyCostText(data),
      filterVariant: 'text' as const,
    },
    {
      header: t('general.claimCosts'),
      value: (data: DTCaseFlowPrivateProxy) => getClaimCostsTotalText(data),
      filterVariant: 'text' as const,
    },
    ...(config.CLAIMS.CLAIMS_FLOW.SERIAL_NUMBER
      ? [
        {
          header: t('general.serialNumber'),
          value: (data: DTCaseFlowPrivateProxy) =>
            data?.facts?.consumer_holding_serial_number ??
            data?.facts?.serial_number ??
            '-',
          filterVariant: 'text' as const,
        },
      ]
      : []),
    {
      header: t('general.reportedFault'),
      value: (data) => data.facts?.consumer_issue_type ?? '-',
    },
    {
      header: t('general.faults'),
      value: (data) => getAssessedFault(data),
      filterVariant: 'text',
    },
    {
      header: t('general.assessment'),
      value: (data) => getWarrantyAssessment(data),
    },
    {
      header: t('general.goodwillOverride'),
      type: 'Status',
      value: (data: DTCaseFlowPrivateProxy) => getGoodwillOverrideFlag(data),
    },
    {
      header: t('general.outcome'),
      value: (data) => data.facts?.claim_outcome ?? '-',
    },
    {
      header: 'Defective part',
      value: (data: DTCaseFlowPrivateProxy) => getDefectivePartText(data),
      filterVariant: 'text' as const,
    },
    {
      header: 'Defective part manufacturer',
      value: (data: DTCaseFlowPrivateProxy) =>
        getDefectivePartManufacturerText(data),
      filterVariant: 'text' as const,
    },
    {
      header: 'Replacement part',
      // If multiple parts exist, only show the first one (per requirement).
      value: (data: DTCaseFlowPrivateProxy) => getReplacementPartText(data),
      filterVariant: 'text' as const,
    },
  ]);
  const addEditModalSettings = {
    title: t('general.editCustomer'),
    handleClose: () => setIsEditCustomerModalOpen(false),
  };

  const confirmationModalSettings = {
    kind: 'pop-up' as const,
    settings: {
      text: 'You are about to delete this customer. You will lose all information. Are you sure you want to continue?',
      primaryButtonText: 'Yes',
      outlineButtonText: 'No, cancel',
      handleClose: () => setIsConfirmationModalOpen(false),
      handlePrimaryClick: async () =>
        console.log('delete --> selectedRowToDelete'),
      handleOutlineClick: () => setIsConfirmationModalOpen(false),
    },
  };

  const renderDetail = (label: string, value: any) => {
    return (
      <div className="flex flex-col" key={label}>
        <p className="font-nunito text-sm text-gray-500">{label}</p>
        <p className="font-nunito mt-2.5 overflow-hidden text-ellipsis text-base">
          {value?.length ? value : '-'}
        </p>
      </div>
    );
  };

  const [favouriteStore, setFavouriteStore] = useState<string>('');

  useEffect(() => {
    if (allHoldings)
      (async () => {
        const res = await getFavouriteStore(allHoldings);
        setFavouriteStore(res);
      })().catch((err) => console.log(err));
  }, [allHoldings]);

  useEffect(() => {
    // If there is a creation attempt error, the customer does not exist, so we redirect to the customers list
    if (error?.class === 'CREATION') navigate('/manage?tab=customers');
  }, []);

  return (
    <div className="flex flex-col gap-5">
      <CardWrapperWithHeader
        title={t('general.customerInformation')}
        headerRight={
          <div className="flex gap-2">
            {/*{config.CUSTOMER.ALLOW_CHANGE_CUSTOMER_EMAIL && (
              <ButtonIcon
                iconLeft={true}
                label={t('general.changeEmail')}
                kind={'bgLightGreen'}
                onClick={() => setIsChangeEmailModalOpen(true)}
                Icon={EnvelopeIcon}
              />
            )}*/}
            <ButtonIcon
              iconLeft={true}
              label={t('general.editCustomer')}
              kind={'bgLightGreen'}
              onClick={() => setIsEditCustomerModalOpen(true)}
              Icon={ArrowTopRightOnSquareIcon}
            />
          </div>
        }
      >
        {!consumerPersonaData || !allHoldings || !favouriteStore ? (
          <div>
            <div className="font-nunito mb-[30px] grid grid-cols-2 gap-8 md:grid-cols-5 lg:grid-cols-5">
              <div className="mb-10">
                {' '}
                <Skeleton count={2} />
              </div>
              <div className="mb-10">
                {' '}
                <Skeleton count={2} />
              </div>
              <div className="mb-10">
                {' '}
                <Skeleton count={2} />
              </div>
              <div className="mb-10">
                {' '}
                <Skeleton count={2} />
              </div>
              <div className="mb-10">
                {' '}
                <Skeleton count={4} />
              </div>
            </div>
            <div className="font-nunito grid grid-cols-2 gap-8 md:grid-cols-5 lg:grid-cols-5">
              <div className="mb-10">
                {' '}
                <Skeleton count={2} />
              </div>
              <div className="mb-10">
                {' '}
                <Skeleton count={2} />
              </div>
              <div className="mb-10">
                {' '}
                <Skeleton count={2} />
              </div>
              <div className="mb-10">
                {' '}
                <Skeleton count={2} />
              </div>
              <div className="mb-10">
                {' '}
                <Skeleton count={4} />
              </div>
            </div>
          </div>
        ) : consumerPersonaData.consumer_type === CORPORATE ? (
          <BusinessDetailsContentSection
            consumer_private={consumerPersonaData}
            holdingsList={allHoldings}
            favouriteStore={favouriteStore}
          />
        ) : (
          <CustomerDetailsContentSection
            consumer_private={consumerPersonaData}
            holdingsList={allHoldings}
            favouriteStore={favouriteStore}
          />
        )}
      </CardWrapperWithHeader>
      {t('tenantLink') === 'SHELTA' && (
        <CardWrapperWithHeader title={'Survey information'}>
          {!consumerPersonaData ? (
            <div className="grid grid-cols-4 gap-10">
              <div className="mb-10">
                {' '}
                <Skeleton count={2} />
              </div>
              <div className="mb-10">
                {' '}
                <Skeleton count={2} />
              </div>
              <div className="mb-10">
                {' '}
                <Skeleton count={2} />
              </div>
              <div className="mb-10">
                {' '}
                <Skeleton count={2} />
              </div>
            </div>
          ) : (
            <div>
              {consumerPersonaData?.gender ? (
                <div className="grid grid-cols-4 gap-10">
                  {renderDetail('Gender', consumerPersonaData?.gender ?? '-')}
                  {renderDetail(
                    'Living situation',
                    consumerPersonaData?.living_situation ?? '-'
                  )}
                  {renderDetail(
                    'Type of dwelling',
                    consumerPersonaData?.type_of_dwelling ?? '-'
                  )}
                  {renderDetail(
                    'Work status',
                    consumerPersonaData?.work_status ?? '-'
                  )}
                </div>
              ) : (
                <Heading kind="p">
                  Survey questionnaire has not been filled.
                </Heading>
              )}
            </div>
          )}
        </CardWrapperWithHeader>
      )}
      <CardWrapperWithHeader
        noPadding={true}
        title={t('general.productsOwnedByTheCustomer')}
      >
        <Table
          columns={productsHeaders(t)}
          data={consumerHoldingsData}
          inner={true}
          initialState={{
            showGlobalFilter: true,
            sorting: [
              {
                id: 'registrationDate', //sort by age by default on page load
                desc: true,
              },
            ],
          }}
          muiSearchTextFieldProps={{
            placeholder: t('general.searchByProductNameCategory'),
          }}
          state={{
            isLoading: !allHoldings,
          }}
          useCallbackClickRow={(data: any) => {
            navigate(SAGE_ROUTE_NAME.REGISTRATIONS + '/' + data.link);
          }}
        />
      </CardWrapperWithHeader>
      <CardWrapperWithHeader
        noPadding={true}
        title={t('general.claimsByCustomer')}
      >
        <ClaimsTable
          initialState={{
            showGlobalFilter: true,
            sorting: [
              {
                id: 'registrationDate', //sort by age by default on page load
                desc: true,
              },
            ],
          }}
          muiSearchTextFieldProps={{
            placeholder: t('general.searchByIDOrProductName'),
          }}
          state={{
            isLoading: !allHoldings,
          }}
          onClickRow={(data) => {
            ONCE(
              () =>
                navigate(SAGE_ROUTE_NAME.CLAIMS + '/' + data.docid, {
                  state: {
                    docid: data.docid,
                    link: data.docid,
                    productName: getProductName(data),
                    extraData: {
                      modelNumber: data.facts?.consumer_holding_serial_number,
                    },
                  },
                }),
              2000
            )();
          }}
        />
      </CardWrapperWithHeader>
      {isEditCustomerModalOpen && (
        <div>
          <ModalNewEditCustomer
            handleClose={() => setIsEditCustomerModalOpen(false)}
            handleDelete={() => setIsConfirmationModalOpen(true)}
            customerDetails={consumerPersonaData}
            modalSettings={addEditModalSettings}
            customerType={consumerPersonaData?.consumer_type}
          />
          {isConfirmationModalOpen && (
            <Modal
              className="h-fit w-[350px] rounded-md bg-white"
              kind={confirmationModalSettings.kind}
              settings={confirmationModalSettings.settings}
            />
          )}
        </div>
      )}

      {isChangeEmailModalOpen && (
        <ModalChangeEmail
          handleClose={() => setIsChangeEmailModalOpen(false)}
          customerDetails={consumerPersonaData}
          modalSettings={{
            title: t('general.changeEmail'),
            handleClose: () => setIsChangeEmailModalOpen(false),
          }}
        />
      )}
      {selectedQuestionnaire && (
        <Modal
          className="lg:w-[400px]"
          kind={'generic'}
          settings={{
            title: t('general.surveyDetails'),
            handleClose: () => setSelectedQuestionnaire(null),
          }}
          children={
            <div className="flex flex-col gap-5 px-5 py-2">
              {selectedQuestionnaire &&
                Object.entries(selectedQuestionnaire).length > 0 &&
                !Object.values(selectedQuestionnaire).every(
                  (value) => value === '' || value === null || value === undefined
                )
                ? Object.entries(selectedQuestionnaire).map(([key, value]) => {
                  const formattedKey = key
                    .replace(/_/g, ' ')
                    .replace(/^\w/, (c) => c.toUpperCase());
                  if (value) return renderDetail(formattedKey, value);
                })
                : t('general.customerDidNotSubmit')}
            </div>
          }
        />
      )}
    </div>
  );
}

export default CustomerDetailView;

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

export const productsHeaders = (t: any) => [
  {
    accessorKey: 'productImage',
    header: t('general.productImage'),
  },
  {
    accessorKey: 'productName',
    header: t('general.productName'),
  },
  {
    header: t('general.category'),
    accessorKey: 'category',
  },
  // {
  //   header: 'purchase date',
  //   accessorKey: 'purchaseDate',
  //   Cell: ({ cell }: any) => {
  //     return cell.getValue() ? formatUnixTime(cell.getValue(), 'dd/MM/yyyy') : '-';
  //   },
  // },
  {
    header: t('general.registrationDate'),
    accessorKey: 'registrationDate',
    Cell: ({ cell }: any) => {
      return formatUnixTime(cell.getValue(), 'dd/MM/yyyy');
    },
  },
  {
    header: t('general.userManual'),
    accessorKey: 'userManual',
  },
  {
    header: t('general.survey'),
    accessorKey: 'survey',
  },
];
